import { faFlask } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Badge,
  Button,
  Divider,
  FormControlLabel,
  FormGroup,
  IconButton,
  Popover,
  Switch,
  TextField,
  Typography,
  styled,
} from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import simulateIncomingCall from './hackathonEvents';

type FeatureToggle = {
  key: string;
  label: string;
  defaultValue: boolean;
};

const TOGGLES: FeatureToggle[] = [
  { key: 'showServiceArea', label: 'Show Service Area in search', defaultValue: true },
  { key: 'showPcp', label: 'Show PCP in search', defaultValue: true },
  { key: 'showProfilePhoto', label: 'Show profile photo in search', defaultValue: false },
  { key: 'collapseEmails', label: 'Collapse emails (show +N)', defaultValue: true },
  { key: 'searchOperators', label: 'Enable search operators (msa:, dob:)', defaultValue: true },
  { key: 'boostRecent', label: 'Boost recently viewed patients', defaultValue: false },
  { key: 'recentlyViewedOnly', label: 'Recently Viewed Patients only', defaultValue: false },
  { key: 'myPanelOnly', label: 'My Panel Patients', defaultValue: false },
  { key: 'simulateCall', label: 'Caller ID banner (simulate calls)', defaultValue: false },
];

const STORAGE_KEY = 'hackathon-toggles';

function loadToggles(): Record<string, boolean> {
  const defaults = Object.fromEntries(TOGGLES.map((t) => [t.key, t.defaultValue]));
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) return { ...defaults, ...JSON.parse(stored) };
  } catch {
    // ignore
  }
  return defaults;
}

function saveToggles(values: Record<string, boolean>) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(values));
}

const StyledPopover = styled(Popover)(({ theme }) => ({
  zIndex: theme.zIndex.tooltip + 1,
  '& .MuiPaper-root': {
    padding: '16px 20px',
    minWidth: '320px',
    marginTop: '4px',
  },
}));

export function useHackathonToggle(key: string): boolean {
  const [value, setValue] = useState(() => loadToggles()[key] ?? true);

  const handleStorageChange = useCallback(() => {
    setValue(loadToggles()[key] ?? true);
  }, [key]);

  useEffect(() => {
    window.addEventListener('hackathon-toggle-change', handleStorageChange);
    return () => window.removeEventListener('hackathon-toggle-change', handleStorageChange);
  }, [handleStorageChange]);

  return value;
}

function HackathonToggles() {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [values, setValues] = useState<Record<string, boolean>>(loadToggles);
  const [callNumber, setCallNumber] = useState('123-456-7809');

  const activeCount = Object.values(values).filter(Boolean).length;

  const handleToggle = (key: string) => (_: React.ChangeEvent, checked: boolean) => {
    const next = { ...values, [key]: checked };
    setValues(next);
    saveToggles(next);
    window.dispatchEvent(new Event('hackathon-toggle-change'));
  };

  return (
    <>
      <IconButton
        size="small"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{ color: 'common.white', ml: 1 }}
        data-cy="hackathon-toggles"
      >
        <Badge badgeContent={activeCount} color="warning" max={99}>
          <FontAwesomeIcon icon={faFlask} />
        </Badge>
      </IconButton>
      <StyledPopover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Typography variant="subtitle2" gutterBottom>
          🧪 Hackathon Toggles
        </Typography>
        <FormGroup>
          {TOGGLES.map((toggle) => (
            <FormControlLabel
              key={toggle.key}
              control={
                <Switch
                  checked={values[toggle.key] ?? toggle.defaultValue}
                  onChange={handleToggle(toggle.key)}
                />
              }
              label={<Typography variant="body2">{toggle.label}</Typography>}
            />
          ))}
        </FormGroup>
        {values.simulateCall && (
          <>
            <Divider sx={{ my: 1.5 }} />
            <Typography variant="subtitle2" gutterBottom>
              📞 Simulate Incoming Patient Call
            </Typography>
            <TextField
              size="small"
              fullWidth
              placeholder="Phone number"
              value={callNumber}
              onChange={(e) => setCallNumber(e.target.value)}
              sx={{ mb: 1 }}
            />
            <Button
              variant="outlined"
              size="small"
              fullWidth
              onClick={() => {
                simulateIncomingCall(callNumber);
                setAnchorEl(null);
              }}
            >
              Ring 📞
            </Button>
          </>
        )}
      </StyledPopover>
    </>
  );
}

export default HackathonToggles;
